import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { createPortal } from 'react-dom'

const HINT_ID = 'locked-field-hint-bubble'
const AUTO_DISMISS_MS = 2800

type HintState = {
  message: string
  top: number
  left: number
  maxWidth: number
}

type LockedFieldHintContextValue = {
  showAtAnchor: (anchor: HTMLElement | null | undefined, message: string) => void
  hide: () => void
}

const LockedFieldHintContext = createContext<LockedFieldHintContextValue | undefined>(undefined)

export function LockedFieldHintProvider({ children }: { children: React.ReactNode }) {
  const [hint, setHint] = useState<HintState | null>(null)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hintVisibleRef = useRef(false)

  const clearTimer = useCallback(() => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const hide = useCallback(() => {
    clearTimer()
    hintVisibleRef.current = false
    setHint(null)
  }, [clearTimer])

  const showAtAnchor = useCallback(
    (anchor: HTMLElement | null | undefined, message: string) => {
      if (!anchor || !message.trim()) return
      const r = anchor.getBoundingClientRect()
      clearTimer()
      hintVisibleRef.current = true
      setHint({
        message,
        top: r.bottom + 8,
        left: r.left,
        maxWidth: Math.max(220, r.width)
      })
      timerRef.current = setTimeout(hide, AUTO_DISMISS_MS)
    },
    [clearTimer, hide]
  )

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') hide()
    }
    const onScroll = () => {
      if (hintVisibleRef.current) hide()
    }
    const onPointerDown = (e: PointerEvent) => {
      if (!hintVisibleRef.current) return
      const t = e.target as Node
      const bubble = document.getElementById(HINT_ID)
      if (bubble?.contains(t)) return
      hide()
    }
    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('pointerdown', onPointerDown, true)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('pointerdown', onPointerDown, true)
    }
  }, [hide])

  const value = useMemo(() => ({ showAtAnchor, hide }), [showAtAnchor, hide])

  const bubble =
    hint &&
    createPortal(
      <div
        id={HINT_ID}
        role="tooltip"
        className="pointer-events-none fixed z-[200] rounded-md border border-gray-200 bg-gray-900 px-3 py-2 text-xs text-white shadow-lg"
        style={{
          top: hint.top,
          left: hint.left,
          maxWidth: Math.min(hint.maxWidth, 320)
        }}
      >
        {hint.message}
      </div>,
      document.body
    )

  return (
    <LockedFieldHintContext.Provider value={value}>
      {children}
      {bubble}
    </LockedFieldHintContext.Provider>
  )
}

export function useLockedFieldHint() {
  const ctx = useContext(LockedFieldHintContext)
  if (!ctx) throw new Error('useLockedFieldHint must be used within LockedFieldHintProvider')
  return ctx
}

/** Spread on native controls when `locked` is true for click/focus hint (no native title delay). */
export function useLockedFieldDomProps(options: { locked: boolean; message: string }) {
  const { showAtAnchor } = useLockedFieldHint()
  const { locked, message } = options
  return useMemo(() => {
    if (!locked) return {}
    const fire = (e: React.SyntheticEvent<HTMLElement>) => {
      showAtAnchor(e.currentTarget, message)
    }
    return {
      onPointerDown: fire,
      onFocus: fire,
      onClick: fire
    }
  }, [locked, message, showAtAnchor])
}
